import { useState, useEffect, useCallback } from 'react';
import Session from 'supertokens-auth-react/recipe/session';

export interface User {
  id: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  role?: string;
  organizationId?: string;
  profilePicture?: string;
}

export interface UseAuthReturn {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  signOut: () => Promise<void>;
  refreshSession: () => Promise<void>;
}

/**
 * Hook for managing authentication state
 *
 * Provides current user info, authentication status, and auth actions.
 */
export function useAuth(): UseAuthReturn {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const loadSession = useCallback(async () => {
    try {
      const sessionExists = await Session.doesSessionExist();

      if (sessionExists) {
        const userId = await Session.getUserId();
        const payload = await Session.getAccessTokenPayloadSecurely();

        setUser({
          id: userId,
          email: payload?.email as string | undefined,
          firstName: payload?.firstName as string | undefined,
          lastName: payload?.lastName as string | undefined,
          role: payload?.role as string | undefined,
          organizationId: payload?.organizationId as string | undefined,
        });
        setIsAuthenticated(true);
      } else {
        setUser(null);
        setIsAuthenticated(false);
      }
    } catch (error) {
      console.error('Failed to load session:', error);
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSession();
  }, [loadSession]);

  const signOut = useCallback(async () => {
    try {
      await Session.signOut();
      setUser(null);
      setIsAuthenticated(false);
      window.location.href = '/login';
    } catch (error) {
      console.error('Failed to sign out:', error);
      throw error;
    }
  }, []);

  const refreshSession = useCallback(async () => {
    setIsLoading(true);
    await loadSession();
  }, [loadSession]);

  return {
    user,
    isAuthenticated,
    isLoading,
    signOut,
    refreshSession,
  };
}

export default useAuth;
